import { CognitoUserPool, type ICognitoStorage } from "amazon-cognito-identity-js";
import { getCognitoConfig } from "@common/cognito-config";

// The prefix amazon-cognito-identity-js puts on every key it stores
// (tokens, LastAuthUser, userData, device keys).
export const COGNITO_KEY_PREFIX = "CognitoIdentityServiceProvider.";

/**
 * Storage that lives only as long as the object. This site hands the tokens
 * to the app in the redirect and keeps nothing itself, so the library must
 * never write them to localStorage (its default).
 */
export class MemoryStorage implements ICognitoStorage {
  private items = new Map<string, string>();

  setItem(key: string, value: string): void {
    this.items.set(key, String(value));
  }

  getItem(key: string): string | null {
    return this.items.has(key) ? this.items.get(key)! : null;
  }

  removeItem(key: string): void {
    this.items.delete(key);
  }

  clear(): void {
    this.items.clear();
  }

  get size(): number {
    return this.items.size;
  }
}

/**
 * A pool backed by its own in-memory storage. A CognitoUser does not inherit
 * the pool's storage (it falls back to localStorage), so pass the returned
 * `storage` as the user's `Storage` too.
 */
export function createUserPool(): { pool: CognitoUserPool; storage: MemoryStorage } {
  const config = getCognitoConfig();
  const storage = new MemoryStorage();
  const pool = new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.clientId,
    Storage: storage,
  });
  return { pool, storage };
}

/**
 * Remove the tokens and email that past sign-ins left in localStorage, from
 * before the pool used in-memory storage. Matches every client id, so keys
 * from an earlier app client go too.
 */
export function clearLeftoverCognitoKeys(storage: Storage | undefined = safeLocalStorage()): void {
  if (!storage) return;
  try {
    const stale: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(COGNITO_KEY_PREFIX)) stale.push(key);
    }
    for (const key of stale) storage.removeItem(key);
  } catch {
    // Storage blocked or unavailable: nothing we can clear.
  }
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}
